// src/contexts/BrandContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useLocation } from "@reach/router";
import { navigate } from "gatsby";
import {
  AvailableBrands,
  availableBrands,
} from "@bosch/bdds.tokens-npm/brands";

const DEFAULT_BRAND: AvailableBrands = "bosch";
const DEFAULT_COLOR_MODE = "light";
const BRAND_BACKGROUND_LINK = "brand-background-link";

export type Theme = {
  $value: string;
  colorMode: string;
  importName: string;
  var: string;
  "figma-type": string;
};

interface BrandContextValue {
  currentBrand: AvailableBrands;
  currentColorMode: string;
  changeTheme: (brand: AvailableBrands, colorMode: string) => void;
  availableBrands: AvailableBrands[];
}

const coreFiles = [
  "gradations/gradations",
  "components/index",
  "semantic/index",
  "semantic/fonts",
];

const semFiles = [
  "mode-primary-schemes",
  "mode-primary-nested-schemes",
  "mode-secondary-schemes",
  "mode-secondary-nested-schemes",
  "mode-contrast-schemes",
  "mode-contrast-nested-schemes",
];

function addBrandCss(brand: AvailableBrands, themes: Theme[]) {
  // Remove existing brand core CSS
  coreFiles.forEach((filename) => {
    const id = `brand-css-${filename.replace("/", "-")}`;
    const el = document.getElementById(id);
    if (el) el.remove();
  });

  // Add new brand core CSS
  coreFiles.forEach((filename) => {
    const link = document.createElement("link");
    link.id = `brand-css-${filename.replace("/", "-")}`;
    link.rel = "stylesheet";
    link.href = `/styles/brands/${brand}/styles/${filename}.css`;

    document.head.appendChild(link);
  });
  // Remove existing brand core CSS
  const brandBackgroundLinks = Array.from(
    document.getElementsByClassName(BRAND_BACKGROUND_LINK)
  );
  brandBackgroundLinks.forEach((el) => {
    if (el) el.remove();
  });

  themes.forEach((theme) => {
    // Add new brand core CSS
    semFiles.forEach((filename) => {
      const link = document.createElement("link");
      link.classList.add(BRAND_BACKGROUND_LINK);
      link.rel = "stylesheet";
      link.href = `/styles/brands/${brand}/styles/backgrounds/${theme.importName}-${filename}.css`;

      document.head.appendChild(link);
    });
  });
}

const BrandContext = createContext<BrandContextValue | undefined>(undefined);

export const BrandProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const lastLoadedBrandRef = useRef<AvailableBrands | null>(null);
  const lastColorMode = useRef(DEFAULT_COLOR_MODE);

  // Derive current brand from URL (single source of truth)
  const currentBrand = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const brandFromUrl = searchParams.get("brand") as AvailableBrands;

    return brandFromUrl && availableBrands.includes(brandFromUrl)
      ? brandFromUrl
      : DEFAULT_BRAND;
  }, [location.search]);

  // Derive current colorMode from URL (single source of truth)
  const currentColorMode = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const colorModeFromUrl = searchParams.get("color-mode");

    return colorModeFromUrl ? colorModeFromUrl : DEFAULT_COLOR_MODE;
  }, [location.search]);

  // Initialize: Set default brand in URL if not present or invalid
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const brandFromUrl = searchParams.get("brand");
    const colorModeFromUrl = searchParams.get("color-mode");

    if (
      !brandFromUrl ||
      !availableBrands.includes(brandFromUrl as AvailableBrands)
    ) {
      searchParams.set("brand", DEFAULT_BRAND);
    }

    if (!colorModeFromUrl) {
      searchParams.set("color-mode", DEFAULT_COLOR_MODE);
    }

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: false,
    });
  }, []);

  // Load brand JSON when brand changes
  useEffect(() => {
    // Skip if we've already loaded this brand
    if (lastLoadedBrandRef.current === currentBrand) {
      console.log(`Brand ${currentBrand} already loaded, skipping`);
      return;
    }

    async function loadBrandAssets() {
      try {
        const brandAssetTokens = await import(
          `/static/styles/brands/${currentBrand}/tokens/sem/asset.tokens.json`
        );
        addBrandCss(currentBrand, brandAssetTokens.sem.asset.themes);
        lastLoadedBrandRef.current = currentBrand;
        console.log(`Loaded brand: ${currentBrand}`);
      } catch (error) {
        console.error(
          `Failed to load brand assets for ${currentBrand}:`,
          error
        );
      }
    }
    loadBrandAssets();
  }, [currentBrand]);

  // change color mode if new is set in URL searchParams
  useEffect(() => {
    document.body.classList.remove(`-${lastColorMode.current}-mode`);
    document.body.classList.add(`-${currentColorMode}-mode`);
    lastColorMode.current = currentColorMode;
  }, [currentColorMode]);

  // Function to change theme
  const changeTheme = (newBrand: AvailableBrands, newColorMode: string) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("brand", newBrand);
    searchParams.set("color-mode", newColorMode);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const value = useMemo(
    () => ({
      currentBrand,
      currentColorMode,
      changeTheme,
      availableBrands,
    }),
    [currentBrand, location]
  );

  return (
    <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
  );
};

// Custom hook to use the brand context
export const useBrand = () => {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error("useBrand must be used within a BrandProvider");
  }
  return context;
};
